import {
  HHAlert,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Paper } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useHingeSelectClient } from '../../api';
import { ConflictTable } from '../components/badges/conflict-table';
import { StatusBadge } from '../components/badges/status-badge';
import { DisabledInfo, StepNav } from '../components/layout/step-nav';
import { TinTabs } from '../components/layout/tin-tabs';
import { SelectableCard } from '../components/selection/selectable-card';
import { ContractInput } from '../context/types';
import { useStepHeader } from '../context/wizard-header.context';
import { useWizardState } from '../context/wizard-state.context';
import { detectMerchantConflicts } from '../utils/conflict-detection';
import { getOrgGroups, OrgGroup } from '../utils/org-groups';

const monoText = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 13,
};

interface MerchantOrgCardProps {
  group: OrgGroup;
  legalName: string;
}

// Per-TIN merchant resolution card. The component owns its own
// resolveMerchantByTin effect, status state slot, and conflict-decision UI.
// The outer step renders one card per distinct TIN — single-TIN input shows
// a single card identical to the pre-PR-2 layout.
const MerchantOrgCard = ({
  group,
  legalName,
}: MerchantOrgCardProps): JSX.Element => {
  const { state, setState } = useWizardState();
  const client = useHingeSelectClient();
  const fired = useRef(false);

  const tin = group.tax_id;
  const medicareNumber = group.medicare_number;
  const resolution = state.resolutions.merchant[tin];

  useEffect(() => {
    if (fired.current || resolution != null) return;
    fired.current = true;
    void (async (): Promise<void> => {
      try {
        const result = await client.resolveMerchantByTin({ tin });
        if (!result.ok) {
          setState(s => ({
            ...s,
            resolutions: {
              ...s.resolutions,
              merchant: {
                ...s.resolutions.merchant,
                [tin]: { status: 'new', reason: result.reason },
              },
            },
          }));
          return;
        }
        const conflicts = detectMerchantConflicts(
          { entityLegalName: legalName, tin },
          result.data.merchant,
        );
        setState(s => ({
          ...s,
          resolutions: {
            ...s.resolutions,
            merchant: {
              ...s.resolutions.merchant,
              [tin]: {
                status: conflicts.length > 0 ? 'conflict' : 'existing',
                data: {
                  merchantId: result.data.merchant.id ?? '',
                  name:
                    result.data.merchant.legalName ??
                    result.data.merchant.name ??
                    '',
                },
                conflictFields: conflicts.length > 0 ? conflicts : undefined,
              },
            },
          },
        }));
      } catch (e) {
        setState(s => ({
          ...s,
          resolutions: {
            ...s.resolutions,
            merchant: {
              ...s.resolutions.merchant,
              [tin]: {
                status: 'conflict',
                conflictFields: [
                  {
                    field: 'network',
                    incoming: tin,
                    existing: (e as Error).message,
                  },
                ],
              },
            },
          },
        }));
      }
    })();
  }, [client, legalName, resolution, setState, tin]);

  const conflictDecision =
    state.decisions.conflictResolutions[`merchant:${tin}`];
  const status = resolution?.status ?? 'pending';

  const setMerchantConflictDecision = (
    decision: 'reuse' | 'override',
  ): void => {
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        conflictResolutions: {
          ...s.decisions.conflictResolutions,
          [`merchant:${tin}`]: decision,
        },
      },
    }));
  };

  // Default conflict resolution to "reuse" — the KG match is the recommended
  // path, and pre-selecting it unblocks the Continue gate for the common case.
  // Guarded on a missing decision so user overrides and rehydrated state win.
  useEffect(() => {
    if (resolution?.status !== 'conflict') return;
    if (conflictDecision != null) return;
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        conflictResolutions: {
          ...s.decisions.conflictResolutions,
          [`merchant:${tin}`]: 'reuse',
        },
      },
    }));
  }, [resolution?.status, conflictDecision, tin, setState]);

  return (
    <HHStack spacing={2}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <HHStack spacing={2}>
          <HHStack direction="row" spacing={4} alignItems="center">
            <StatusBadge status={status} />
            <HHTypography hhVariant="subtitle1">{legalName}</HHTypography>
          </HHStack>
          <HHStack direction="row" spacing={12}>
            <HHStack spacing={0.5}>
              <HHTypography hhVariant="caption" sx={{ opacity: 0.6 }}>
                Tax ID (TIN)
              </HHTypography>
              <HHTypography hhVariant="body2" sx={monoText}>
                {tin}
              </HHTypography>
            </HHStack>
            <HHStack spacing={0.5}>
              <HHTypography hhVariant="caption" sx={{ opacity: 0.6 }}>
                Locations
              </HHTypography>
              <HHTypography hhVariant="body2" sx={monoText}>
                {group.locations.length}
              </HHTypography>
            </HHStack>
            {medicareNumber && (
              <HHStack spacing={0.5}>
                <HHTypography hhVariant="caption" sx={{ opacity: 0.6 }}>
                  Medicare number
                </HHTypography>
                <HHTypography hhVariant="body2" sx={monoText}>
                  {medicareNumber}
                </HHTypography>
              </HHStack>
            )}
            {resolution?.data?.merchantId && (
              <HHStack spacing={0.5}>
                <HHTypography hhVariant="caption" sx={{ opacity: 0.6 }}>
                  Merchant ID
                </HHTypography>
                <HHTypography hhVariant="body2" sx={monoText}>
                  {resolution.data.merchantId}
                </HHTypography>
              </HHStack>
            )}
          </HHStack>
        </HHStack>
      </Paper>

      {status === 'conflict' && resolution?.conflictFields != null && (
        <HHAlert hhVariant="outlined" severity="error">
          <HHStack spacing={1.5}>
            <HHTypography hhVariant="subtitle2">
              Conflict — incoming attributes disagree with the existing merchant
            </HHTypography>
            <ConflictTable conflicts={resolution.conflictFields} />
            <div role="radiogroup" aria-label="Resolve merchant conflict">
              <HHStack spacing={1}>
                <SelectableCard
                  selected={conflictDecision === 'reuse'}
                  onClick={(): void => setMerchantConflictDecision('reuse')}
                  recommended
                  recommendedLabel="Recommended · KG match"
                >
                  <HHTypography hhVariant="body1">Reuse existing</HHTypography>
                  <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
                    Skip the create-merchant write and link this onboarding to
                    the merchant the KG already has.
                  </HHTypography>
                </SelectableCard>
                <SelectableCard
                  selected={conflictDecision === 'override'}
                  onClick={(): void => setMerchantConflictDecision('override')}
                >
                  <HHTypography hhVariant="body1">Override</HHTypography>
                  <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
                    Write the incoming attributes with MANUAL source rank.
                    Overwrites any non-MANUAL fields on the existing record.
                  </HHTypography>
                </SelectableCard>
              </HHStack>
            </div>
          </HHStack>
        </HHAlert>
      )}
    </HHStack>
  );
};

interface MerchantStepInnerProps {
  input: ContractInput;
}

const MerchantStepInner = ({ input }: MerchantStepInnerProps): JSX.Element => {
  const { state } = useWizardState();
  const orgGroups = getOrgGroups(input);
  const [activeTin, setActiveTin] = useState<string>(
    () => orgGroups[0]?.tax_id ?? '',
  );

  useStepHeader({
    description:
      orgGroups.length > 1
        ? `Resolve ${orgGroups.length} provider organizations (one per distinct TIN). Each conflict requires an explicit reuse-vs-override decision.`
        : 'Resolve the provider organization (Merchant) by TIN. Conflicts require an explicit reuse-vs-override decision.',
  });

  const isReadyForTin = (tin: string): boolean => {
    const r = state.resolutions.merchant[tin];
    if (r == null) return false;
    if (r.status === 'existing' || r.status === 'new') return true;
    if (r.status === 'conflict') {
      return state.decisions.conflictResolutions[`merchant:${tin}`] != null;
    }
    return false;
  };

  // Per-TIN gating: every group must reach a terminal status (existing/new),
  // and any conflicts must have an explicit reuse/override decision.
  const allReady = orgGroups.every(g => isReadyForTin(g.tax_id));

  const anyPending = orgGroups.some(
    g =>
      (state.resolutions.merchant[g.tax_id]?.status ?? 'pending') === 'pending',
  );
  const anyUnresolvedConflict = orgGroups.some(g => {
    const r = state.resolutions.merchant[g.tax_id];
    return (
      r?.status === 'conflict' &&
      state.decisions.conflictResolutions[`merchant:${g.tax_id}`] == null
    );
  });

  const canContinue = allReady;
  const disabledInfo: DisabledInfo | undefined = canContinue
    ? undefined
    : anyPending
      ? {
          message: 'Resolving merchant(s) against the Knowledge Graph…',
          severity: 'info',
        }
      : anyUnresolvedConflict
        ? {
            message:
              'One or more merchants have conflicts. Choose Reuse or Override for each before continuing.',
            severity: 'warning',
          }
        : undefined;

  return (
    <HHStack spacing={3}>
      <TinTabs
        items={orgGroups.map(g => ({
          group: g,
          legalName:
            state.decisions.orgNames[g.tax_id] ?? input.entity_legal_name,
          ready: isReadyForTin(g.tax_id),
          // Amber warning: KG returned a conflict and the operator hasn't yet
          // chosen reuse or override for this TIN.
          conflict:
            state.resolutions.merchant[g.tax_id]?.status === 'conflict' &&
            state.decisions.conflictResolutions[`merchant:${g.tax_id}`] == null,
        }))}
        activeTin={activeTin}
        onSelect={setActiveTin}
      />

      {orgGroups.map(g => {
        const isActive = g.tax_id === activeTin || orgGroups.length === 1;
        if (!isActive) return null;
        return (
          <MerchantOrgCard
            key={g.tax_id}
            group={g}
            legalName={
              state.decisions.orgNames[g.tax_id] ?? input.entity_legal_name
            }
          />
        );
      })}

      <StepNav
        id="merchant"
        canContinue={canContinue}
        disabledInfo={disabledInfo}
      />
    </HHStack>
  );
};

export const MerchantStep = (): JSX.Element => {
  const { state } = useWizardState();
  if (state.input == null) return <Navigate to="../intake" replace />;
  return <MerchantStepInner input={state.input} />;
};
